import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { NightWalk } from './NightWalk';
import { ToggleSwitch } from './ToggleSwitch';
import { Button } from './buttons/Button';
import { buildData, logoutUser } from '../api';
import { BoneIcon } from '../assets/BoneIcon';
import { HamburgerMenu } from '../assets/HamburgerMenu';
import { useAuth } from '../context/AuthContext';
import {
  THEME_BANNER_EMOJIS,
  THEME_OPTIONS,
  ThemeChoice,
  resolveThemeName,
  useTheme,
} from '../context/ThemeContext';
import { useIsFridayAfternoon } from '../hooks/useIsFridayAfternoon';
import { useOutsideHandler } from '../hooks/useOutsideHandler';
import { useZoomies } from '../hooks/useZoomies';

export const Nav = ({ breadcrumbs }: any) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { isDark, toggleTheme, themeChoice, setThemeChoice, fxEnabled, setFxEnabled } = useTheme();
  const [shouldShowContextMenu, setShouldShowContextMenu] = React.useState(false);
  const contextMenuRef = React.useRef<HTMLDivElement | null>(null);
  const { isZooming, handleToggle: handleThemeToggle } = useZoomies(toggleTheme);
  // Easter egg: after 4pm on Fridays the hamburger icon turns into a bone.
  const isFridayAfternoon = useIsFridayAfternoon();

  // Close the menu on any click outside the hamburger button + menu.
  useOutsideHandler(contextMenuRef, () => setShouldShowContextMenu(false));

  if (!isAuthenticated) return <></>;

  const bannerEmojis = THEME_BANNER_EMOJIS[resolveThemeName(themeChoice)];

  return (
    <div className="flex justify-between bg-secondary border-b border-black p-3 relative">
      <div className="flex items-center gap-4">
        <img
          onClick={() => {
            location.href = '/';
          }}
          className={`w-28 cursor-pointer dark:invert ${isZooming ? 'bark-zoomies' : ''}`}
          src={require('../assets/fullBarkLogo.png')}
          alt="Logo"
        />
        {bannerEmojis && (
          <span aria-hidden="true" className="text-2xl select-none tracking-widest">
            {bannerEmojis}
          </span>
        )}
      </div>
      <div className="flex items-center">
        {breadcrumbs.length > 1 && <ol className="flex mr-2">{breadcrumbs}</ol>}
        <div ref={contextMenuRef}>
          <a
            onClick={() => {
              setShouldShowContextMenu(!shouldShowContextMenu);
            }}
            className="cursor-pointer"
            title={isFridayAfternoon ? "Happy Friday! The weekend's almost here 🦴" : undefined}
          >
            {isFridayAfternoon ? (
              <BoneIcon className="w-10 h-10 friday-bone" />
            ) : (
              <HamburgerMenu className="w-10 h-10" />
            )}
          </a>
          {shouldShowContextMenu && (
            <div className="absolute right-3 top-14 bg-white shadow-lg rounded-lg p-4 w-250 z-10 border border-gray-200">
              <div className="text-sm text-gray-700 mb-2">
                <strong>Email:</strong> {localStorage.getItem('email')?.replace(/"/g, '')}
              </div>
              <div className="text-sm text-gray-700 mb-2">
                <strong>Build Date:</strong> {buildData.FE_BUILD_DATE.split('T')[0]}
              </div>
              <div className="text-sm text-gray-700 mb-2">
                <strong>Build Version:</strong> {buildData.FE_GH_COMMIT_HASH}
              </div>
              <ToggleSwitch
                id="dark-mode-toggle"
                label="Dark Mode"
                checked={isDark}
                onChange={handleThemeToggle}
              />
              <div className="flex items-center justify-between text-sm text-gray-700 mb-2">
                <label htmlFor="theme-select" className="cursor-pointer">
                  <strong>Theme</strong>
                </label>
                <select
                  id="theme-select"
                  value={themeChoice}
                  onChange={(e) => setThemeChoice(e.target.value as ThemeChoice)}
                  className="border border-gray-300 rounded text-sm py-1 px-2 cursor-pointer"
                >
                  {THEME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {resolveThemeName(themeChoice) !== 'default' && (
                <ToggleSwitch
                  id="falling-effects-toggle"
                  label="Falling effects"
                  checked={fxEnabled}
                  onChange={() => setFxEnabled(!fxEnabled)}
                />
              )}
              <hr className="my-2" />
              <Button
                variant="secondary"
                label="Logout"
                onClick={async () => {
                  await logoutUser().catch((e) => console.error(e));
                  localStorage.removeItem('token');
                  logout();
                  navigate('/login');
                }}
                className="block w-full text-left px-2 py-2 text-sm font-medium  rounded"
              />
            </div>
          )}
        </div>
      </div>
      <NightWalk />
    </div>
  );
};
